import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import supabase from '../integrations/supabase';
import { CourseSelectionSkeleton } from '../components/SkeletonLoader';
import { useOnboardingTheme } from '../utils/theme-context';

// Add shimmer animation CSS
const shimmerAnimation = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  .animate-shimmer {
    animation: shimmer 2s infinite;
  }
`;

const CourseSelection = () => {
  const { user, registerOAuthUser, checkUserCourseSelection, setHasCourseSelection, forceRefreshCourseSelection } = useAuth();
  const navigate = useNavigate();
  
  // Force light mode for onboarding
  useOnboardingTheme();
  const [userRole, setUserRole] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]); // For teachers
  const [selectedClass, setSelectedClass] = useState(null); // For students
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1); // 1: Role, 2: Class, 3: Subject
  const [registrationAttempted, setRegistrationAttempted] = useState(false);
  const [savingSelection, setSavingSelection] = useState(false);



  useEffect(() => {
    let isMounted = true;
    let hasInitialized = false;
    
    const initializePage = async () => {
      // Prevent multiple initializations
      if (hasInitialized) return;
      hasInitialized = true;
      

      
      try {
        setLoading(true);
        
        // Check auth first
        await checkAuth();
        if (!isMounted) return;
        
        // Register OAuth user with backend if needed (only once)
        if (user && !registrationAttempted) {
          try {
            await registerOAuthUser(user);
            setRegistrationAttempted(true);
          } catch (error) {

          }
        }
        
        // Fetch data in parallel only if not already loaded
        if (classes.length === 0 || subjects.length === 0) {

          const [userRoleResult, courseDataResult] = await Promise.all([
            fetchUserRole(),
            fetchCourseData()
          ]);
          
          if (!isMounted) return;
        }
        

        setLoading(false);
      } catch (error) {
        console.error('Error initializing course selection page:', error);
        if (isMounted) {
          setError('Failed to initialize page');
          setLoading(false);
        }
      }
    };
    

    
    if (window.location.pathname === '/course-selection') {
    
      initializePage();
    }
    
    return () => {
      isMounted = false;
    };
  }, [user, registrationAttempted]); // Add dependencies to ensure proper initialization

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/login');
      return;
    }

  };

  const fetchUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;


      
      const { data: userRole } = await supabase
        .from('user_roles')
        .select('*')
        .eq('email', user.email)
        .single();

      setUserRole(userRole);
      
      // If user already has a role, skip to step 2
      if (userRole) {

        setCurrentStep(2);
      } else {
        
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      // Don't set error here as it might be expected for new users
    }
  };

  const fetchCourseData = async () => {
    try {

      
      // Fetch classes - sort by class_order for proper numerical order
      const { data: classesData } = await supabase
        .from('master_classes')
        .select('*')
        .eq('is_active', true)
        .order('class_order');

      

      setClasses(classesData || []);
      
      // Fetch all subjects from database
      const { data: subjectsData } = await supabase
        .from('master_class_subject_books')
        .select('*')
        .eq('is_active', true)
        .order('class_id, subject_name, book_name');

      
      
      setSubjects(subjectsData || []);
    } catch (error) {
      console.error('Error fetching course data:', error);
      throw error; // Let the parent handle the error
    }
  };



  const handleRoleSelection = async (role) => {
    try {

      setUserRole({ role_type: role });
      
      // Save role to database
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Get role_id from master_roles
        const { data: masterRole } = await supabase
          .from('master_roles')
          .select('id')
          .eq('role_name', role)
          .single();
        
        if (masterRole) {
          await supabase
            .from('user_roles')
            .insert({
              email: user.email,
              role_id: masterRole.id
            });
        }
      }
      
      // Move to next step
      setCurrentStep(2);
    } catch (error) {
      console.error('Error saving role:', error);
      setError('Failed to save role selection');
    }
  };

  const handleClassSelection = (classId) => {
    
    
    if (userRole?.role_type === 'teacher') {
      setSelectedClasses(prev => {
        const newSelection = prev.includes(classId) 
          ? prev.filter(id => id !== classId)
          : [...prev, classId];
        
        // Automatically proceed to step 3 if at least one class is selected
        if (newSelection.length > 0) {
          setTimeout(() => setCurrentStep(3), 300);
        }
        
        return newSelection;
      });
    } else {
      setSelectedClass(classId);
      // Automatically proceed to step 3 for students
      setCurrentStep(3);
    }
  };

  const handleSubjectSelection = async (subjectId) => {
    
    
    setSelectedSubjects(prev => {
      if (prev.includes(subjectId)) {
        return prev.filter(id => id !== subjectId);
      } else {
        return [...prev, subjectId];
      }
    });
  };

  const handleProceedWithSubjects = async () => {
    // Prevent multiple saves
    if (savingSelection) {

      return;
    }
    
    try {
      setSavingSelection(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;



      // Get selected subject and book information for saving
      const selectedSubjectData = selectedSubjects.map(subjectId => {
        const subject = subjects.find(s => s.id === subjectId);
        return subject ? {
          subject_name: subject.subject_name,
          book_name: subject.book_name
        } : null;
      }).filter(Boolean);

      // Get selected class name
      const selectedClassName = userRole?.role_type === 'teacher' 
        ? selectedClasses.map(classId => classes.find(c => c.id === classId)?.class_name).filter(Boolean).join(', ')
        : classes.find(c => c.id === selectedClass)?.class_name;

      

      // Save to the existing course_selections table
      const { error } = await supabase
        .from('course_selections')
        .upsert({
          user_id: user.id,
          selected_class: selectedClassName,
          selected_stream: userRole?.role_type || 'student',
          selected_subjects: selectedSubjectData,
          full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('📚 Error saving to course_selections:', error);
        throw new Error(`Failed to save course selection: ${error.message}`);
      }

      
      
      // Longer delay to ensure database update is processed
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      

      
      // Direct database check to verify data was saved
      try {
        const dataCheckResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/v1/course-selections/check-saved-data/${user.id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('user_token')}`,
            'x_user_token': localStorage.getItem('user_token'),
            'Content-Type': 'application/json'
          }
        });
        const dataCheckResult = await dataCheckResponse.json();

        
        if (dataCheckResult.data_exists) {
          
        } else {
          
        }
      } catch (dataCheckError) {
        console.error('🔍 Data check error:', dataCheckError);
      }
      
      // Force refresh the course selection status in the auth context with retry
      
      
      // Retry mechanism for verification
      let hasSelection = false;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (!hasSelection && retryCount < maxRetries) {
        retryCount++;
        
        
        hasSelection = await forceRefreshCourseSelection();
        
        if (!hasSelection && retryCount < maxRetries) {
          
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // Force update the auth context state to ensure it's properly set
      if (hasSelection) {

        
        // Small delay to ensure state update is processed
        setTimeout(() => {
          // Use React Router navigation instead of window.location.href
          const targetRoute = userRole?.role_type === 'teacher' ? '/teacher-dashboard' : '/dashboard';
  
          navigate(targetRoute);
        }, 200);
      } else {
        console.error('❌ Failed to verify course selection was saved after retries');
        setError('Failed to verify course selection. Please try again.');
        setSavingSelection(false);
      }
    } catch (error) {
      console.error('Error saving selections:', error);
      setError('Failed to save selections: ' + error.message);
      setSavingSelection(false);
    }
  };

  const handleNextStep = () => {
    // This function is no longer used since we auto-proceed
    // Keeping it for potential future use
  };

  const handleContinue = async () => {
    // This function is no longer used since we auto-proceed
    // Keeping it for potential future use
  };

  const getSubjectsForClass = (classId) => {
    // This function is no longer used since we filter directly
    // Keeping it for potential future use
    const subjectsForClass = subjects.filter(subject => subject.class_id === classId);
    
    return subjectsForClass;
  };



  if (loading) {
    
    return <CourseSelectionSkeleton />;
  }

  if (classes.length === 0 || subjects.length === 0) {
    
    return <CourseSelectionSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{shimmerAnimation}</style>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-4">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    currentStep >= step 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {step}
                  </div>
                  {step < 3 && (
                    <div className={`w-16 h-1 mx-2 ${
                      currentStep > step ? 'bg-blue-600' : 'bg-gray-300'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-center mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Step {currentStep} of 3: {
                  currentStep === 1 ? 'Select Your Role' :
                  currentStep === 2 ? 'Select Your Class' :
                  'Select Your Subjects'
                }
              </p>
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
              {currentStep === 1 ? '📚 Personal Selection' : 
               currentStep === 2 ? '📚 Class Selection' : 
               `📚 Subject Selection${selectedSubjects.length > 0 ? ` (${selectedSubjects.length} selected)` : ''}`}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
              {currentStep === 1 ? 'Let\'s set up your learning journey' :
               currentStep === 2 ? 'Choose your class level' :
               'Select your subjects'}
            </p>
            
            {/* Compact User Info */}
            {user && (
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full px-4 py-2 text-white text-sm font-medium">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span>{user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}</span>
              </div>
            )}
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6"
            >
              {error}
            </motion.div>
          )}

          {/* Step 1: Role Selection */}
          {currentStep === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                I am a...
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleRoleSelection('student')}
                  className="p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-green-300 cursor-pointer transition-all duration-200"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Student</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      I learn and study subjects
                    </p>
                  </div>
                </motion.div>
                
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleRoleSelection('teacher')}
                  className="p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-blue-300 cursor-pointer transition-all duration-200"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Teacher</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      I teach classes and manage student learning
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  className="p-6 rounded-xl border-2 border-gray-300 dark:border-gray-600 cursor-not-allowed opacity-50 transition-all duration-200"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-400 dark:text-gray-500 mb-2">School</h3>
                    <p className="text-gray-400 dark:text-gray-500">
                      Coming soon...
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Class Selection */}
          {currentStep === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {userRole?.role_type === 'teacher' ? 'Select Classes You Teach' : 'Select Your Class'}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {classes.map((cls) => (
                  <motion.div
                    key={cls.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleClassSelection(cls.id)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      (userRole?.role_type === 'teacher' ? selectedClasses.includes(cls.id) : selectedClass === cls.id)
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        (userRole?.role_type === 'teacher' ? selectedClasses.includes(cls.id) : selectedClass === cls.id)
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {(userRole?.role_type === 'teacher' ? selectedClasses.includes(cls.id) : selectedClass === cls.id) && (
                          <div className="w-full h-full rounded-full bg-white scale-50"></div>
                        )}
                      </div>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {cls.class_name}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>


            </motion.div>
          )}

          {/* Step 3: Subject Selection */}
          {currentStep === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {userRole?.role_type === 'teacher' ? 'Select Subjects You Teach' : 'Select Your Subjects'}
              </h2>
              
              {/* Selected Class Display */}
              <div className="mb-4 flex items-center justify-center gap-3">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-black rounded-full px-4 py-2 text-white text-sm font-medium">
                  <div className="w-4 h-4 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <span>
                    {userRole?.role_type === 'teacher' 
                      ? selectedClasses.map(classId => {
                          const selectedClass = classes.find(c => c.id === classId);
                          return selectedClass?.class_name;
                        }).filter(Boolean).join(', ')
                      : classes.find(c => c.id === selectedClass)?.class_name
                    }
                  </span>
                </div>
                
                {/* Navigation Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                  >
                    <div className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      Change Class
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                  >
                    <div className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      Back
                    </div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {(() => {
                  // Debug logging
                  
                  
                  // Get the selected class name
                  const selectedClassName = classes.find(c => c.id === selectedClass)?.class_name;
                  
                  
                  const filteredSubjects = subjects.filter(subject => 
                    userRole?.role_type === 'teacher' 
                      ? selectedClasses.includes(subject.class_id)
                      : subject.class_id === selectedClass
                  );
                  
                  
                  
                  // Show all subjects without grouping
                  return filteredSubjects.map((subject) => (
                    <motion.div
                      key={subject.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSubjectSelection(subject.id)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 relative ${
                        selectedSubjects.includes(subject.id)
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-md'
                          : 'border-gray-200 dark:border-gray-700 hover:border-green-300 hover:shadow-sm'
                      }`}
                    >
                      {/* Selection indicator */}
                      {selectedSubjects.includes(subject.id) && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      
                      <div className="flex items-start gap-2.5">
                        <div className={`w-3.5 h-3.5 rounded-full border-2 mt-0.5 flex-shrink-0 ${
                          selectedSubjects.includes(subject.id)
                            ? 'border-green-500 bg-green-500'
                            : 'border-gray-300'
                        }`}>
                          {selectedSubjects.includes(subject.id) && (
                            <div className="w-full h-full rounded-full bg-white scale-50"></div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm text-gray-900 dark:text-white leading-tight mb-1">
                            {subject.subject_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-600 dark:text-gray-300">Book:</span>{' '}
                            <span className="text-gray-700 dark:text-gray-200">{subject.book_name}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ));
                })()}
              </div>
              
              {/* Subject Loading State */}
              {subjects.filter(subject => 
                userRole?.role_type === 'teacher' 
                  ? selectedClasses.includes(subject.class_id)
                  : subject.class_id === selectedClass
              ).length === 0 && (
                <div className="text-center py-8">
                  <div className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <div className="relative overflow-hidden w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded-full">
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        initial={{ x: '-100%' }}
                        animate={{ x: '100%' }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                      />
                    </div>
                    <span>Loading subjects...</span>
                  </div>
                </div>
              )}

              {/* Show message if no subjects available */}
              {subjects.filter(subject => 
                userRole?.role_type === 'teacher' 
                  ? selectedClasses.includes(subject.class_id)
                  : subject.class_id === selectedClass
              ).length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400">
                    No subjects available for the selected class. You can still continue without selecting subjects.
                  </p>
                </div>
              )}

              {/* Proceed Button */}
              <div className="mt-8 flex flex-col items-center">
                {/* Selection Summary */}
                {selectedSubjects.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 text-center"
                  >
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full px-4 py-2 text-white text-sm font-medium">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>
                        {selectedSubjects.length} subject{selectedSubjects.length !== 1 ? 's' : ''} selected
                      </span>
                    </div>
                  </motion.div>
                )}
                
                {/* Engaging Proceed Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleProceedWithSubjects}
                  disabled={savingSelection || (selectedSubjects.length === 0 && userRole?.role_type !== 'teacher')}
                  className={`relative overflow-hidden px-8 py-4 rounded-xl text-lg font-bold text-white transition-all duration-300 ${
                    selectedSubjects.length > 0 || userRole?.role_type === 'teacher'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  {savingSelection ? (
                    <div className="flex items-center gap-3">
                      <div className="relative overflow-hidden w-5 h-5 bg-white/20 rounded-full">
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                          initial={{ x: '-100%' }}
                          animate={{ x: '100%' }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                        />
                      </div>
                      <span>Saving Your Selection...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                                              <span>
                          {selectedSubjects.length > 0 
                            ? `🚀 Start Learning with ${selectedSubjects.length} Subject${selectedSubjects.length !== 1 ? 's' : ''}!`
                            : userRole?.role_type === 'teacher'
                            ? '🚀 Proceed to Teacher Dashboard!'
                            : 'Select at least one subject to continue'
                          }
                        </span>
                    </div>
                  )}
                  
                  {/* Animated background effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer"></div>
                </motion.button>
                
                {/* Encouraging message */}
                {selectedSubjects.length > 0 ? (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-3 text-sm text-gray-600 dark:text-gray-400 text-center max-w-md"
                  >
                    Ready to embark on your learning journey? Let's get started with your personalized study experience! 📚✨
                  </motion.p>
                ) : (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-3 text-sm text-gray-500 dark:text-gray-400 text-center max-w-md"
                  >
                    💡 Tip: Select one or more subjects to customize your learning experience
                  </motion.p>
                )}
              </div>


            </motion.div>
          )}
        </motion.div>
      </div>

    </div>
    </>
  );
};

export default CourseSelection; 